import React, { useEffect, useState, useMemo } from "react";
import { Check, X, Trash2, Eye, Mail, Phone, MapPin, Package, ShieldCheck, Inbox, Filter, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_BADGE = {
    pending: "bg-amber-50 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-50 text-[#E53935]",
    closed: "bg-[#F4F1EA] text-[#4A5568]",
    fulfilled: "bg-blue-50 text-blue-800",
};

const ServicesPanel = () => {
    const [rows, setRows] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending");  // default to moderation queue
    const [activeOrders, setActiveOrders] = useState(null);  // {svc, list}

    const load = async () => {
        setLoading(true);
        try {
            const [r, o] = await Promise.all([
                api.get("/admin/services", { params: statusFilter ? { status: statusFilter } : {} }),
                api.get("/admin/services/orders"),
            ]);
            setRows(r.data || []);
            setOrders(o.data || []);
        } catch (e) {
            toast.error("Could not load services");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

    const approve = async (svc) => {
        try {
            await api.post(`/admin/services/${svc.id}/approve`);
            toast.success(`Approved "${svc.title}" — lister notified`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Approve failed");
        }
    };

    const reject = async (svc) => {
        const reason = window.prompt("Reason for rejection (sent to the lister)?", "Did not meet community guidelines.");
        if (reason === null) return;
        try {
            await api.post(`/admin/services/${svc.id}/reject`, { rejection_reason: reason });
            toast.success("Rejected — lister notified");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reject failed");
        }
    };

    const remove = async (svc) => {
        if (!window.confirm(`Delete "${svc.title}" and all its orders?`)) return;
        try {
            await api.delete(`/admin/services/${svc.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const openOrders = (svc) => {
        setActiveOrders({ svc, list: orders.filter((o) => o.service_id === svc.id) });
    };

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0, closed: 0, fulfilled: 0 };
        rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
        return c;
    }, [rows]);

    return (
        <div className="space-y-6" data-testid="services-admin-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Community board · moderation</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Approve, reject, or remove free offerings and community needs. Approved listings appear on the public <code>/services</code> board. Each order CC&apos;s <code>accounts@</code> and <code>sales@reddogfoundation.org</code> for transparency.</p>
                </div>
                <p className="text-xs text-[#4A5568] flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" strokeWidth={1.5} /> {orders.length} total orders across {rows.length} listings</p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] inline-flex items-center gap-1"><Filter className="w-3 h-3" /> Status:</span>
                {[
                    { v: "pending", label: "Pending review" },
                    { v: "approved", label: "Approved" },
                    { v: "rejected", label: "Rejected" },
                    { v: "closed", label: "Sold out" },
                    { v: "fulfilled", label: "Fulfilled" },
                    { v: "", label: "All" },
                ].map((opt) => (
                    <button key={opt.v} onClick={() => setStatusFilter(opt.v)} data-testid={`services-filter-${opt.v || "all"}`} className={`text-xs px-3 py-1 rounded-full transition ${statusFilter === opt.v ? "bg-[#E53935] text-white font-medium" : "bg-white border border-black/10 text-[#4A5568] hover:bg-[#F4F1EA]"}`}>
                        {opt.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="services-admin-empty">No listings with this status.</p>
            ) : (
                <ul className="space-y-3" data-testid="services-admin-list">
                    {rows.map((r) => {
                        const orderCount = orders.filter((o) => o.service_id === r.id).length;
                        return (
                            <li key={r.id} data-testid={`service-admin-row-${r.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] || "bg-gray-100"}`}>{r.status}</span>
                                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#4A5568]">{r.kind === "request" ? "NEED" : "FREE OFFER"}</span>
                                            <span className="text-[10px] text-[#4A5568]">{r.category}</span>
                                        </div>
                                        <p className="font-medium text-[#1C252E]">{r.title}</p>
                                        <p className="text-xs text-[#4A5568] line-clamp-2 mt-0.5">{r.description}</p>
                                        <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-[#4A5568]">
                                            <span><Package className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5} /> {r.quantity_available}/{r.quantity_total} {r.unit_label}</span>
                                            <span><MapPin className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5} /> {r.address}</span>
                                            <span title="Lister"><Mail className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5} /> {r.lister_email}</span>
                                            {r.lister_phone && <span><Phone className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5} /> {r.lister_phone}</span>}
                                            {r.business_name && <span><ShieldCheck className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5} /> {r.business_name}</span>}
                                        </div>
                                        {r.rejection_reason && r.status === "rejected" && (
                                            <p className="mt-2 text-xs bg-red-50 border border-red-200 rounded-lg px-2 py-1 text-[#7A2E2A]"><strong>Rejected:</strong> {r.rejection_reason}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {r.status === "pending" && (
                                            <>
                                                <button onClick={() => approve(r)} data-testid={`service-approve-${r.id}`} className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-full px-3 py-1.5 font-medium">
                                                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> Approve
                                                </button>
                                                <button onClick={() => reject(r)} data-testid={`service-reject-${r.id}`} className="inline-flex items-center gap-1 text-xs bg-[#1C252E] hover:bg-black text-white rounded-full px-3 py-1.5 font-medium">
                                                    <X className="w-3.5 h-3.5" strokeWidth={2} /> Reject
                                                </button>
                                            </>
                                        )}
                                        <a href={`/services/${r.id}`} target="_blank" rel="noopener noreferrer" data-testid={`service-view-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="View public page">
                                            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                        </a>
                                        <button onClick={() => openOrders(r)} data-testid={`service-orders-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] relative" aria-label="View orders">
                                            <Inbox className="w-4 h-4" strokeWidth={1.5} />
                                            {orderCount > 0 && <span className="absolute -top-1 -right-1 bg-[#E53935] text-white rounded-full text-[9px] w-4 h-4 flex items-center justify-center font-bold">{orderCount}</span>}
                                        </button>
                                        <button onClick={() => remove(r)} data-testid={`service-delete-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10" aria-label="Delete">
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {activeOrders && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="service-orders-modal">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-black/5">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Orders for this listing</p>
                                <h3 className="font-heading text-lg text-[#1C252E] truncate">{activeOrders.svc.title}</h3>
                                <p className="text-xs text-[#4A5568] mt-0.5">{activeOrders.list.length} order{activeOrders.list.length === 1 ? "" : "s"} · Foundation CC&apos;d on every email</p>
                            </div>
                            <button onClick={() => setActiveOrders(null)} className="p-2 rounded-full hover:bg-[#F4F1EA]" aria-label="Close">
                                <X className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {activeOrders.list.length === 0 ? (
                                <p className="text-sm text-[#4A5568] italic">No orders yet.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">
                                            <tr>
                                                <th className="py-2 pr-3">When</th>
                                                <th className="py-2 pr-3">Requester</th>
                                                <th className="py-2 pr-3">Contact</th>
                                                <th className="py-2 pr-3">Qty</th>
                                                <th className="py-2 pr-3">Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeOrders.list.map((o) => (
                                                <tr key={o.id} className="border-t border-black/5">
                                                    <td className="py-2 pr-3 text-xs text-[#4A5568] whitespace-nowrap">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{o.requester_name}</td>
                                                    <td className="py-2 pr-3 text-xs text-[#4A5568]">
                                                        <div>{o.requester_email}</div>
                                                        <div>{o.requester_phone}</div>
                                                        <div>{o.requester_address}</div>
                                                    </td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{o.quantity} {o.unit_label}</td>
                                                    <td className="py-2 pr-3 text-[#4A5568] text-xs max-w-xs whitespace-pre-line">{o.message || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServicesPanel;
